import { zodResolver } from '@hookform/resolvers/zod';
import {
  accountSessionSchema,
  enableTwoFactorSchema,
  okSchema,
  passwordConfirmSchema,
  passwordSchema,
  recoveryCodesSchema,
  twoFactorSetupSchema,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Loader2, MonitorSmartphone, ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { applyServerErrors, errorMessage } from '@/lib/forms';
import { describeUserAgent, timeAgo } from '@/lib/format';
import { sessionQuery, useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/account/security')({ component: SecurityPage });

function SecurityPage() {
  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <PasswordCard />
        <TwoFactorCard />
      </div>
      <SessionsCard />
    </div>
  );
}

const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: passwordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: "Passwords don't match.",
  });

function PasswordCard() {
  const queryClient = useQueryClient();
  type Values = z.input<typeof passwordFormSchema>;
  const form = useForm<Values>({ resolver: zodResolver(passwordFormSchema) });
  const save = useMutation({
    mutationFn: ({ currentPassword, newPassword }: Values) =>
      apiRequest('/api/client/account/password', {
        method: 'PUT',
        body: { currentPassword, newPassword },
        schema: okSchema,
      }),
    onSuccess: async () => {
      form.reset({ currentPassword: '', newPassword: '', confirm: '' });
      await queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] });
      toast.success('Password changed. Your other sessions were signed out.');
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Password</CardTitle>
        <CardDescription>Changing it signs out all your other sessions.</CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardContent className="grid gap-4">
          <FormField
            label="Current password"
            error={form.formState.errors.currentPassword?.message}
          >
            <Input
              type="password"
              autoComplete="current-password"
              {...form.register('currentPassword')}
            />
          </FormField>
          <FormField
            label="New password"
            error={form.formState.errors.newPassword?.message}
            description="At least 10 characters."
          >
            <Input type="password" autoComplete="new-password" {...form.register('newPassword')} />
          </FormField>
          <FormField label="Confirm new password" error={form.formState.errors.confirm?.message}>
            <Input type="password" autoComplete="new-password" {...form.register('confirm')} />
          </FormField>
        </CardContent>
        <CardFooter className="mt-6 justify-end">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />} Change password
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function TwoFactorCard() {
  const { user, twoFactorSetupRequired } = useAuthenticatedSession();
  const [setupOpen, setSetupOpen] = useState(false);
  const [confirm, setConfirm] = useState<null | 'disable' | 'regenerate'>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Two-factor authentication
          {user.twoFactorEnabled ? (
            <Badge className="bg-success/15 text-success border-success/30">
              <ShieldCheck /> On
            </Badge>
          ) : (
            <Badge variant="outline">
              <ShieldOff /> Off
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {user.twoFactorEnabled
            ? 'Sign-ins require a code from your authenticator app or a recovery code.'
            : 'Protect your account with a code from an authenticator app (1Password, Authy, Google Authenticator…).'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {twoFactorSetupRequired && (
          <p className="text-warning text-sm">
            This panel requires administrators to use two-factor authentication.
          </p>
        )}
      </CardContent>
      <CardFooter className="mt-auto justify-end gap-2">
        {user.twoFactorEnabled ? (
          <>
            <Button variant="outline" onClick={() => setConfirm('regenerate')}>
              New recovery codes
            </Button>
            <Button variant="destructive" onClick={() => setConfirm('disable')}>
              Disable
            </Button>
          </>
        ) : (
          <Button onClick={() => setSetupOpen(true)}>Set up</Button>
        )}
      </CardFooter>

      {setupOpen && (
        <SetupDialog
          onClose={() => setSetupOpen(false)}
          onEnabled={(c) => {
            setSetupOpen(false);
            setCodes(c);
          }}
        />
      )}
      {confirm && (
        <ConfirmPasswordDialog
          mode={confirm}
          onClose={() => setConfirm(null)}
          onCodes={(c) => {
            setConfirm(null);
            setCodes(c);
          }}
        />
      )}
      {codes && <RecoveryCodesDialog codes={codes} onClose={() => setCodes(null)} />}
    </Card>
  );
}

function SetupDialog({
  onClose,
  onEnabled,
}: {
  onClose: () => void;
  onEnabled: (codes: string[]) => void;
}) {
  const queryClient = useQueryClient();
  const setup = useQuery({
    queryKey: ['account', '2fa-setup'],
    queryFn: () =>
      apiRequest('/api/client/account/two-factor', {
        method: 'POST',
        schema: twoFactorSetupSchema,
      }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  type Values = z.input<typeof enableTwoFactorSchema>;
  const form = useForm<Values>({ resolver: zodResolver(enableTwoFactorSchema) });
  const enable = useMutation({
    mutationFn: (body: Values) =>
      apiRequest('/api/client/account/two-factor/enable', {
        method: 'POST',
        body,
        schema: recoveryCodesSchema,
      }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      onEnabled(res.recoveryCodes);
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set up two-factor authentication</DialogTitle>
          <DialogDescription>
            Scan the QR code with your authenticator app, then enter the 6-digit code it shows.
          </DialogDescription>
        </DialogHeader>
        <QueryState query={setup}>
          {(data) => (
            <form
              id="enable-2fa"
              onSubmit={form.handleSubmit((v) => enable.mutate(v))}
              noValidate
              className="grid gap-4"
            >
              <div
                className="mx-auto size-48 rounded-md bg-white p-2 [&_svg]:size-full"
                // The SVG is generated server-side by the qrcode library from our own otpauth URI.
                dangerouslySetInnerHTML={{ __html: data.qrSvg }}
                role="img"
                aria-label="QR code for your authenticator app"
              />
              <p className="text-muted-foreground text-center text-xs">
                Can&apos;t scan? Enter this key manually:
                <code className="bg-muted text-foreground mt-1 block rounded px-2 py-1 font-mono text-xs break-all select-all">
                  {data.secret}
                </code>
              </p>
              <FormField label="6-digit code" error={form.formState.errors.code?.message}>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  {...form.register('code')}
                />
              </FormField>
              <FormField
                label="Current password"
                error={form.formState.errors.currentPassword?.message}
              >
                <Input
                  type="password"
                  autoComplete="current-password"
                  {...form.register('currentPassword')}
                />
              </FormField>
            </form>
          )}
        </QueryState>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="enable-2fa" disabled={enable.isPending || !setup.isSuccess}>
            {enable.isPending && <Loader2 className="animate-spin" />} Enable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmPasswordDialog({
  mode,
  onClose,
  onCodes,
}: {
  mode: 'disable' | 'regenerate';
  onClose: () => void;
  onCodes: (codes: string[]) => void;
}) {
  const queryClient = useQueryClient();
  type Values = z.input<typeof passwordConfirmSchema>;
  const form = useForm<Values>({ resolver: zodResolver(passwordConfirmSchema) });
  const act = useMutation({
    mutationFn: async (body: Values) => {
      if (mode === 'disable') {
        await apiRequest('/api/client/account/two-factor/disable', {
          method: 'POST',
          body,
          schema: okSchema,
        });
        return null;
      }
      return (
        await apiRequest('/api/client/account/two-factor/recovery-codes', {
          method: 'POST',
          body,
          schema: recoveryCodesSchema,
        })
      ).recoveryCodes;
    },
    onSuccess: async (codes) => {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      if (codes) onCodes(codes);
      else {
        toast.success('Two-factor authentication disabled.');
        onClose();
      }
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'disable'
              ? 'Disable two-factor authentication?'
              : 'Generate new recovery codes?'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'disable'
              ? 'Your account will be protected by your password alone.'
              : 'Your existing recovery codes will stop working.'}
          </DialogDescription>
        </DialogHeader>
        <form id="confirm-password" onSubmit={form.handleSubmit((v) => act.mutate(v))} noValidate>
          <FormField
            label="Current password"
            error={form.formState.errors.currentPassword?.message}
          >
            <Input
              type="password"
              autoComplete="current-password"
              autoFocus
              {...form.register('currentPassword')}
            />
          </FormField>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="confirm-password"
            variant={mode === 'disable' ? 'destructive' : 'default'}
            disabled={act.isPending}
          >
            {act.isPending && <Loader2 className="animate-spin" />}{' '}
            {mode === 'disable' ? 'Disable' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecoveryCodesDialog({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success('Recovery codes copied.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save your recovery codes</DialogTitle>
          <DialogDescription>
            Each code works once if you lose access to your authenticator app. They won&apos;t be
            shown again.
          </DialogDescription>
        </DialogHeader>
        <ul className="bg-muted grid grid-cols-2 gap-2 rounded-md p-4 font-mono text-sm">
          {codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={() => void copy()}>
            <Copy /> Copy
          </Button>
          <Button onClick={onClose}>I&apos;ve saved them</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionsCard() {
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: ['account', 'sessions'],
    queryFn: ({ signal }) =>
      apiRequest('/api/client/account/sessions', {
        schema: z.object({ data: z.array(accountSessionSchema) }),
        signal,
      }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] });
  const revoke = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/client/account/sessions/${id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const revokeOthers = useMutation({
    mutationFn: () =>
      apiRequest('/api/client/account/sessions/revoke-others', {
        method: 'POST',
        schema: z.object({ revoked: z.number() }),
      }),
    onSuccess: async (res) => {
      await refresh();
      toast.success(`Signed out ${res.revoked} other session${res.revoked === 1 ? '' : 's'}.`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Sessions</CardTitle>
          <CardDescription>Browsers currently signed in to your account.</CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => revokeOthers.mutate()}
          disabled={revokeOthers.isPending}
        >
          Sign out other sessions
        </Button>
      </CardHeader>
      <CardContent>
        <QueryState query={sessions}>
          {({ data }) => (
            <ul className="divide-y">
              {data.map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-3">
                  <MonitorSmartphone className="text-muted-foreground size-5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {describeUserAgent(s.userAgent)}
                      {s.current && <Badge variant="secondary">This device</Badge>}
                      {s.impersonated && <Badge variant="outline">Admin impersonation</Badge>}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {s.ip ?? 'unknown IP'} · active {timeAgo(s.lastSeenAt)} · signed in{' '}
                      {timeAgo(s.createdAt)}
                    </div>
                  </div>
                  {!s.current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revoke.mutate(s.id)}
                      disabled={revoke.isPending}
                    >
                      Sign out
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </QueryState>
      </CardContent>
    </Card>
  );
}
